import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import {
  EmailInput,
  PasswordInput,
  GeneralSelectInput,
  TextInput,
} from "components/molecules";
import { useAuth } from "contexts/AuthContext/AuthContext";
import { UserType } from "types";

const SignUpSchema = Yup.object().shape({
  userType: Yup.string()
    .oneOf(["STUDENT", "PROVIDER", "PARTNER", "EDUBAY"])
    .required("User type is required"),
  username: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm Password is required"),
  domain: Yup.string().when("userType", {
    is: (userType: UserType) => userType === "PROVIDER" || userType === "PARTNER",
    then: (schema) =>
      schema
        .required("Domain is required")
        .matches(
          /^(?!:\/\/)(?!www\.)[a-zA-Z0-9-_]+(\.[a-zA-Z0-9-_]+)+$/,
          "Please enter a valid domain without http://www."
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const SignUpForm: React.FC = () => {
  const { authState, setAuthState, handleSignUp } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const savedOnboardingData = sessionStorage.getItem("onboardingData");
  const onboardingData = savedOnboardingData
    ? JSON.parse(savedOnboardingData)
    : null;

  const handleSignUpError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    setErrorMessage(errorMessage);
  };

  const handleSuccessfulSignUp = (): void => {
    signUpFormik.resetForm();
  };

  const handleSignUpSubmit = async (values: {
    userType: UserType;
    username: string;
    password: string;
    confirmPassword: string;
    domain: string;
  }) => {
    try {
      values.userType = authState?.userType || UserType.STUDENT; // Default as Student when signup
      
      const error = await handleSignUp({
        userType: values.userType,
        username: values.username,
        password: values.password,
        domain: values.domain
      });

      if (error) {
        handleSignUpError(error);
        return;
      }

      handleSuccessfulSignUp();
    } catch (error) {
      handleSignUpError(error);
    }
  };

  const signUpFormik = useFormik({
    initialValues: {
      userType: authState?.userType || UserType.STUDENT,
      username: onboardingData?.email || "",
      password: "",
      confirmPassword: "",
      domain: "",
    },
    validationSchema: SignUpSchema,
    onSubmit: handleSignUpSubmit,
  });

  useEffect(() => {
    setAuthState((prevState) => ({
      ...prevState,
      userType: signUpFormik.values.userType,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUpFormik.values.userType]);

  const toSignIn = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
    }));
  };

  return (
    <form className="auth-form" onSubmit={signUpFormik.handleSubmit}>
      <div className="form-group">
        <h2>Sign Up</h2>
      </div>

      <GeneralSelectInput
        id="userType"
        label="User Group"
        options={[
          { value: "STUDENT", label: "Students" },
          { value: "PROVIDER", label: "Education Institutions" },
          { value: "PARTNER", label: "Recruitment Partners" },
          { value: "EDUBAY", label: "Edubay" },
        ]}
        onChange={signUpFormik.handleChange}
        onBlur={signUpFormik.handleBlur}
        value={signUpFormik.values.userType}
        touched={signUpFormik.touched.userType}
        error={signUpFormik.errors.userType}
      />

      <EmailInput
        id="username"
        label="Email"
        getFieldProps={signUpFormik.getFieldProps}
        touched={!!signUpFormik.touched.username}
        error={signUpFormik.errors.username as string}
      />

      <PasswordInput
        label="Password"
        id="password"
        placeholder="Please enter password"
        getFieldProps={signUpFormik.getFieldProps}
        touched={signUpFormik.touched.password}
        error={signUpFormik.errors.password}
      />

      <PasswordInput
        label="Confirm Password"
        id="confirmPassword"
        placeholder="Confirm your password"
        getFieldProps={signUpFormik.getFieldProps}
        touched={signUpFormik.touched.confirmPassword}
        error={signUpFormik.errors.confirmPassword}
      />

      {signUpFormik.values.userType === "PROVIDER" ||
      signUpFormik.values.userType === "PARTNER" ||
      signUpFormik.values.userType === "EDUBAY" ? (
        <TextInput
          label="Domain"
          id="domain"
          placeholder="Enter your company domain"
          helperText="Enter the domain without 'http://www.' (e.g., 'example.com')"
          getFieldProps={signUpFormik.getFieldProps}
          touched={signUpFormik.touched.domain}
          error={signUpFormik.errors.domain}
        />
      ) : null}

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Sign Up"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Go to Sign In"
        onClick={toSignIn}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default SignUpForm;
